/* eslint-disable no-console */

import type { Notification } from "@prisma/client";
import debounce from "lodash/debounce";
import { useEffect, useRef } from "react";
import { useRevalidator } from "react-router";
import { toast } from "react-toastify";
import type { SessionUser } from "~/.server/lib/auth";
import { NotificationToast } from "~/components/presentation/NotificationToast";

function connect() {
  const appUrl = window.env.APP_URL;
  const wsUrl = appUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const protocol = appUrl.startsWith("https") ? "wss" : "ws";
  const ws = new WebSocket(`${protocol}://${wsUrl}`);
  ws.onopen = () => {
    console.log("Connected to WebSocket server");
  };
  return ws;
}

export function useWSConnection({ user }: { user: SessionUser | null }) {
  const userRef = useRef<SessionUser | null>(user);
  const wsRef = useRef<WebSocket | null>(null);
  const validator = useRevalidator();

  const revalidateMessages = useRef(
    debounce(() => {
      // Debounce to avoid too many revalidations in a short time
      validator.revalidate();
    }, 500)
  );

  // Establish WebSocket connection on mount
  useEffect(() => {
    if (
      // Don't reconnect if already connected and the user is the same
      userRef.current?.id == user?.id &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN
    ) {
      return;
    }

    if (!user) {
      // If user is not authenticated, close the WebSocket connection if it exists
      wsRef.current?.close();
      wsRef.current = null;
      userRef.current = null;
      return;
    }

    const ws = connect();
    wsRef.current = ws;
    userRef.current = user;

    // Handle incoming messages
    ws.onmessage = (event) => {
      const { type, data } = JSON.parse(event.data);
      // Based on the type of message, handle it accordingly
      if (type == "notification") {
        // Show a toast notification for the new notification
        toast(NotificationToast, {
          data: data as Notification,
          className: "p-0",
        });
      }
      if (type == "message") {
        // If there is a new message while the user is on the messages page, revalidate all the data
        // This will pull the latest messages and threads and also update the unread count etc
        if (location.pathname.endsWith("/messages")) {
          revalidateMessages.current();
        }
      }
    };

    ws.onclose = () => {
      console.log("Disconnected from WebSocket server");
      wsRef.current = null;
    };

    return () => {
      // Clean up WebSocket connection on unmount
      wsRef.current?.close();
    };
  }, [user]);
}
